import {
	SxProps,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Theme,
} from "@mui/material";
import { ChangeEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import TableSort, { SortDirection } from "../TableSort/TableSort";

export interface TableColumn<DataType> {
	label?: string;
	minWidth?: number;
	width?: number | string;
	format: (row: DataType) => ReactNode;
	align?: "right" | "left" | "center";
	sx?: SxProps<Theme>;
	isSortable?: boolean;
	fieldName?: string;
}

interface TableComponentProps<
	DataType extends { id: string | number; type?: string } = { id: string | number; type?: string }
> {
	columns: TableColumn<DataType>[];
	data: DataType[];
	isLoading?: boolean;
	page: number;
	limit: number;
	itemCount: number;
	handleChangePage: (event: unknown, page: number) => void;
	handleChangeLimit: (event: ChangeEvent<HTMLInputElement>) => void;
	noDataText?: string;
	allowNavigation?: boolean;
	hidePagination?: boolean;
	hideHeader?: boolean;
	rowSx?: SxProps<Theme>;
	headerSx?: SxProps<Theme>;
	tableSx?: SxProps<Theme>;
	bodySx?: SxProps<Theme>;
	sortColumn?: string;
	sortDirection?: SortDirection;
	setSortDirection?: (fieldName: string, sortDirection?: SortDirection) => void;
}

export function TableComponent<DataType extends { id: string | number; type?: string }>({
	columns,
	data,
	page,
	limit,
	itemCount,
	handleChangePage,
	handleChangeLimit,
	noDataText,
	allowNavigation,
	hidePagination,
	hideHeader,
	bodySx = {},
	rowSx = {},
	tableSx = {},
	headerSx = {},
	setSortDirection,
	sortDirection,
	sortColumn,
}: TableComponentProps<DataType>) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<>
			<Table stickyHeader aria-label="sticky table" sx={tableSx}>
				{hideHeader ? null : (
					<TableHead
						sx={{
							backgroundColor: "#F4F3F1",
						}}
					>
						<TableRow
							sx={{
								...headerSx,
							}}
						>
							{columns.map((column, index) => (
								<TableCell
									key={index}
									align={column.align}
									className="!text-[14px] !font-urbanist !font-normal !text-subtle !py-4 !leading-5 !capitalize"
									style={{ minWidth: column.minWidth, width: column.width }}
									sx={column?.sx ?? {}}
								>
									<span className="font-urbanist">
										{column.label}
										{column?.isSortable && (
											<TableSort
												fieldName={column.fieldName}
												setSortDirection={setSortDirection}
												sortColumn={sortColumn}
												sortDirection={sortDirection}
											/>
										)}
									</span>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
				)}

				<TableBody className="custom-scrollbar" sx={bodySx}>
					{data && data.length > 0 ? (
						data.map((row) => (
							<TableRow
								key={`${row.id}${row.type ?? ""}`}
								sx={{
									cursor: allowNavigation ? "pointer" : "",
									...rowSx,
								}}
								tabIndex={-1}
								onClick={() => (allowNavigation ? navigate(`${row.id}`) : null)}
							>
								{columns.map((column, index) => (
									<TableCell
										key={index}
										align={column.align}
										style={{
											minWidth: column.minWidth,
											width: column.width,
											whiteSpace: column.width === "fit-content" ? "nowrap" : "initial",
										}}
										sx={{
											...(column?.sx ?? {}),
										}}
									>
										{column.format(row)}
									</TableCell>
								))}
							</TableRow>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length}>{noDataText ?? t("basics.noData")}</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>

			{data && data.length > 0 && !hidePagination && (
				<TablePagination
					SelectProps={{
						MenuProps: {
							PaperProps: {
								sx: {
									borderRadius: "12px",
									boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
									border: "1px solid #DCDAD6",
									padding: "8px",
									backgroundColor: "#fff",
									"&.Mui-focused": {
										borderRadius: "12px !important",
									},
								},
							},
							sx: {
								"& .MuiMenu-list": {
									padding: "0px",
								},
								"& .MuiMenuItem-root": {
									fontSize: "14px",
									color: "#11110F",
									"&:hover": {
										backgroundColor: "#DCDAD6",
									},
									"&.Mui-selected": {
										backgroundColor: "transparent !important",
										color: "#11110F",
									},
								},
							},
						},
						sx: {
							"& .MuiSelect-select": {
								padding: "12px 24px 12px 16px",
								borderRadius: "8px",
								border: "1px solid #DCDAD6",
								"&:focus": {
									borderRadius: "12px !important",
									borderColor: "#FF5A24",
									borderWidth: "1px",
									boxShadow: "none",
								},
							},
							"& .MuiSvgIcon-root": {
								color: "#686761",
							},
						},
					}}
					className="!font-urbanist"
					component="div"
					count={itemCount ?? 0}
					labelDisplayedRows={({ from, to, count }) => (
						<span className="!text-[14px] !font-urbanist !font-medium !text-primary-blue-dark">
							{from} - {to} of {count !== -1 ? count : `more than ${to}`}
						</span>
					)}
					labelRowsPerPage={
						<span className="!text-[14px] !font-urbanist !font-medium !text-subtle">
							{t("basics.rows-per-page")}
						</span>
					}
					page={Math.max(page - 1, 0)}
					rowsPerPage={limit}
					rowsPerPageOptions={[10, 25, 50, 100]}
					sx={{
						marginTop: "24px",
						"& .MuiTablePagination-toolbar": {
							display: "flex",
							justifyContent: "space-between",
						},
						"& .MuiInputBase-root": {
							fontSize: "14px",
							color: "#11110F",
							marginRight: "20px",
						},
						"& .MuiSelect-select": {
							padding: "12px 24px 12px 16px",
							borderRadius: "12px",
							border: "1px solid #DCDAD6",
						},
						"& .MuiSvgIcon-root": {
							color: "#686761",
						},
						"& .MuiSelect-icon": {
							right: "0px",
							color: "#11110F",
							top: "calc(50% - 9px)",
						},
						"& .MuiTablePagination-actions": {
							display: "flex",
							alignItems: "center",
							gap: "8px",
						},
						"& .MuiTablePagination-actions button": {
							color: "#B8431A",
							borderRadius: "12px",
							border: "1px solid #DCDAD6",
							"&:hover": {
								backgroundColor: "#DCDAD6",
							},
						},
					}}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeLimit}
				/>
			)}
		</>
	);
}
